import axios from 'axios';

// Determine base URL based on environment
const getBaseURL = () => {
  if (process.env.NODE_ENV === 'production') {
    return 'https://6c4s0a9lqg.execute-api.ap-south-1.amazonaws.com/api';
  }
  return 'http://localhost:8000';
};

// Create axios instance with base URL
const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// API service functions
export const apiService = {
  // Health check
  getHealth: () => api.get('/'),

  // Upload
  uploadFile: async (file, filename, seasonInfo = null) => {
    // Convert file to base64
    const fileBase64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    return api.post('/upload', {
      file: fileBase64,
      filename: filename,
      content_type: file.type,
      season_name: seasonInfo?.name,
      season_id: seasonInfo?.id,
    });
  },

  // Players
  getPlayers: () => api.get('/players'),
  getSeasonPlayers: () => api.get(`/season_players`),
  registerPlayer: (registrationData) => api.post('/players', registrationData),

  // Seasons
  getSeasons: () => api.get('/seasons'),
  createSeason: (seasonData) => api.post('/seasons', seasonData),

  // Teams
  getTeams: () => api.get('/teams'),
  createTeam: (teamData) => api.post('/teams', teamData),

  // Sponsors
  getSponsors: (seasonIdOrActiveOnly = null, activeOnly = true, registrationsOnly = false) => {
    let seasonId = null;
    if (typeof seasonIdOrActiveOnly === 'boolean') {
      activeOnly = seasonIdOrActiveOnly;
    } else {
      seasonId = seasonIdOrActiveOnly;
    }

    const params = new URLSearchParams();
    if (seasonId) {
      params.set('season_id', seasonId);
    }
    if (activeOnly) {
      params.set('active', 'true');
    }
    if (registrationsOnly) {
      params.set('registrations', 'true');
    }

    const query = params.toString();
    return api.get(`/sponsors${query ? `?${query}` : ''}`);
  },
  createSponsor: (sponsorData) => api.post('/sponsors', sponsorData),
  updateSponsor: (sponsorId, sponsorData) => api.put(`/sponsors/${sponsorId}`, sponsorData),

  // Season Teams
  getSeasonAllTeams: () => api.get(`/season_teams`, { timeout: 30000 }),
  getSeasonTeams: (seasonId) => api.get(`/season_teams/${seasonId}`),
  createSeasonTeam: (teamData) => api.post('/season_teams', teamData),

  // Season Match Data (Points Table)
  getSeasonPointsTable: (seasonId) => api.get(`/season_match_data/${seasonId}`),
  getPlayerMatchData: (puuid) => api.get(`/player_match_data/${puuid}`),
  createSeasonMatchData: (matchData) => api.post('/season_match_data', matchData),

  // Season Player Data (Statistics)
  getSeasonPlayerData: (seasonId) => api.get(`/season_player_data/${seasonId}`),

  // Groups
  getGroups: () => api.get('/groups'),
  createGroup: (groupData) => api.post('/groups', groupData),

  // Group Stages
  getGroupStages: (seasonId) => api.get(`/group_stages/${seasonId}`),
  assignTeamToGroup: (assignmentData) => api.post('/group_stages', assignmentData),

  // Fixtures
  getFixtures: (seasonId) => api.get(`/fixtures/${seasonId}`),
  getGroupFixtures: (seasonId, groupId) => api.get(`/fixtures/group/${seasonId}/${groupId}`),
  createMatch: (matchData) => api.post('/fixtures', matchData),
  updateMatch: (matchId, matchData) => api.put(`/fixtures/update/${matchId}`, matchData),

  // All Matches with Player Stats
  getAllMatches: (seasonId) => api.get(`/all_matches/${seasonId}`, { timeout: 30000 }),


  // Auction
  auctionSetup: (data) => api.post('/auction/setup', data),
  getAuctionState: (seasonId) => api.get(`/auction/state/${seasonId}`),
  getAuctionBudgets: (sessionId) => api.get(`/auction/budgets/${sessionId}`),
  getAuctionSets: (sessionId) => api.get(`/auction/sets/${sessionId}`),
  getAuctionResults: (sessionId) => api.get(`/auction/results/${sessionId}`),
  getAuctionPool: (seasonId) => api.get(`/auction/pool/${seasonId}`),
  endAuction: (sessionId, adminPassword) => api.post('/auction/end', { session_id: sessionId, admin_password: adminPassword }),
  revertSold: (sessionId, auctionPlayerId, adminPassword) => api.post('/auction/revert', { session_id: sessionId, auction_player_id: auctionPlayerId, admin_password: adminPassword }),
  verifyPlayer: (seasonPlayerId, verified) => api.post('/season_player/verify', { season_player_id: seasonPlayerId, verified }),
  setCurrentPlayer: (sessionId, auctionPlayerId, password) => api.post('/auction/admin/set-player', { session_id: sessionId, auction_player_id: auctionPlayerId }, { headers: { 'X-Admin-Password': password } }),
  updateBid: (sessionId, amount, password) => api.post('/auction/admin/bid', { session_id: sessionId, amount }, { headers: { 'X-Admin-Password': password } }),
  markSold: (sessionId, teamId, finalAmount, password) => api.post('/auction/admin/sold', { session_id: sessionId, team_id: teamId, final_amount: finalAmount }, { headers: { 'X-Admin-Password': password } }),
  markUnsold: (sessionId, password) => api.post('/auction/admin/unsold', { session_id: sessionId }, { headers: { 'X-Admin-Password': password } }),
};

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export default api;